import { useState } from "react";
import { ChevronLeft, ChevronRight, Umbrella, BookOpen, GraduationCap, PencilRuler } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const REF_MONDAY = new Date("2024-01-01T00:00:00Z");

export function weekLetter(ds) {
  const weeks = Math.floor((new Date(ds + "T00:00:00Z") - REF_MONDAY) / (7 * 86400000));
  return ((weeks % 2) + 2) % 2 === 0 ? "A" : "B";
}

export default function CalendarView({ holidays = [], homework = [], exams = [], weeks = 1 }) {
  const [offset, setOffset] = useState(0);
  const base = new Date();
  const view = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  const holidayOn = (ds) => holidays.find((h) => h.start <= ds && ds <= h.end);
  const hwOn = (ds) => homework.filter((h) => h.due_date === ds);
  const examsOn = (ds) => exams.filter((e) => e.date === ds);

  return (
    <div className="nb-card p-5" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setOffset(offset - 1)} data-testid="calendar-prev"
          className="p-2 rounded-xl border-2 border-zinc-900 bg-white hover:bg-zinc-100 transition-colors duration-150">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="font-display font-black text-xl" data-testid="calendar-title">{MONTHS[month]} {year}</h3>
        <button onClick={() => setOffset(offset + 1)} data-testid="calendar-next"
          className="p-2 rounded-xl border-2 border-zinc-900 bg-white hover:bg-zinc-100 transition-colors duration-150">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className={`grid gap-1 ${weeks === 2 ? "grid-cols-[auto_repeat(7,1fr)]" : "grid-cols-7"}`}>
        {weeks === 2 && <div className="w-7" />}
        {DOW.map((d) => <div key={d} className="text-center text-[11px] font-bold text-zinc-400 py-1">{d}</div>)}
        {rows.map((row, ri) => {
          const firstDay = row.find((x) => x !== null);
          const rowDs = firstDay ? `${year}-${String(month + 1).padStart(2, "0")}-${String(firstDay).padStart(2, "0")}` : null;
          return [
            weeks === 2 && (
              <div key={`w${ri}`} className="w-7 flex items-center justify-center">
                <span className="text-[10px] font-black text-zinc-400 border border-zinc-200 rounded px-1">
                  {rowDs ? weekLetter(rowDs) : ""}
                </span>
              </div>
            ),
            ...row.map((d, ci) => {
              if (d === null) return <div key={`e${ri}-${ci}`} />;
              const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const hol = holidayOn(ds);
              const hw = hwOn(ds);
              const ex = examsOn(ds);
              const isToday = ds === today;
              const daysUntil = Math.round((new Date(ds + "T00:00:00") - new Date(today + "T00:00:00")) / 86400000);
              const isStudy = hol && hol.kind === "study";
              const titleParts = [];
              if (hol) titleParts.push(hol.name);
              ex.forEach((e) => titleParts.push(`Exam: ${e.title}${e.start_time ? " " + e.start_time : ""}`));
              hw.forEach((h) => titleParts.push(`Due: ${h.text}`));
              return (
                <div key={ds} data-testid={`calendar-day-${ds}`} title={titleParts.join("\n") || undefined}
                  className={`min-h-[52px] rounded-lg border-2 p-1 text-xs font-bold transition-colors duration-150 ${
                    hol ? (isStudy ? "border-zinc-900 bg-[#7DD3FC]/50" : "border-zinc-900 bg-[#F9A8D4]/60") : isToday ? "border-zinc-900 bg-[#A7E4D3]" : "border-zinc-200 bg-white"
                  }`}>
                  <div>{d}</div>
                  {hol && <div className="text-[9px] leading-tight font-bold text-zinc-600 flex items-center gap-0.5 mt-0.5"><Umbrella className="w-2.5 h-2.5 shrink-0" />{hol.name}</div>}
                  {ex.map((e) => (
                    <div key={e.id} className="mt-0.5 flex items-center gap-0.5 text-[9px] font-bold text-indigo-900">
                      <GraduationCap className="w-2.5 h-2.5 shrink-0" />{e.title}{e.start_time ? ` ${e.start_time}` : ""}{daysUntil > 0 && daysUntil <= 30 ? ` · in ${daysUntil}d` : ""}
                    </div>
                  ))}
                  {hw.length > 0 && (
                    <div className="mt-0.5 flex items-center gap-0.5 text-[9px] font-bold text-zinc-700">
                      <BookOpen className="w-2.5 h-2.5 shrink-0" />{hw.length} due
                    </div>
                  )}
                </div>
              );
            }),
          ];
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-[11px] font-bold text-zinc-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-zinc-900 bg-[#F9A8D4]/60 inline-block" /> School closed</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-zinc-900 bg-[#7DD3FC]/50 inline-block" /> Study day off</span>
        <span className="inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3 text-indigo-900" /> Exam</span>
        <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> Homework due</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-zinc-900 bg-[#A7E4D3] inline-block" /> Today</span>
      </div>
    </div>
  );
}
